import React from 'react';
import { useSelector } from 'react-redux';
import { M } from '@dashboard-experience/mastodon';
import { i18n } from '@international/mastodon-i18n';
import { ReportSummary } from 'api/candidates/types/Report';
import I18nLink from 'components/I18nLink/I18nLink';
import { REPORTS_PAGE } from 'identifiers/identifiers';
import ReportsHeader from '../ReportListHeader';
import ReportCard from '../ReportCard';
import './ReportList.scss';
import sortByOptions, { ReportsOrderBy } from '../lib/sortReportCardsBy';
import isActionRequired from '../lib/isActionRequired';

const EmptyState: React.FC<{ applicantId: string | null }> = ({
  applicantId,
}) => {
  return (
    <>
      {applicantId ? (
        <I18nLink
          translationKey='components.CandidateLandingPage.noReportsMessageExpired'
          className='empty-reports'
          id='no-reports-message'
        />
      ) : (
        <p className='empty-reports' id='no-reports-message'>
          {i18n.getStr(
            'components.CandidateLandingPage.noReportsMessageNoRecord',
          )}
        </p>
      )}
      <I18nLink
        translationKey='components.CandidateLandingPage.noReportsInternationalHint'
        className='empty-reports'
        id='international-portal-hint'
        linkTarget='_self'
      />
    </>
  );
};

export const ReportList: React.FC<{ reports: ReportSummary[] }> = ({
  reports,
}) => {
  const applicantId = useSelector((state: any) => state.portal.applicantId);
  const [actionRequiredCount, setActionRequiredCount] =
    React.useState<number>(0);
  const [sortBy, setSortBy] = React.useState<ReportsOrderBy>('status');

  React.useEffect(() => {
    if (reports.length) {
      setActionRequiredCount(
        reports.filter(report => isActionRequired(report)).length,
      );
    }
  }, [reports]);

  return (
    <M.Container.Col>
      {reports.length > 0 && (
        <ReportsHeader
          reportCount={reports.length}
          actionRequiredCount={actionRequiredCount}
          sortedBy={sortBy}
          onSortChange={(e: React.MouseEvent<HTMLInputElement>) => {
            const { value } = e.target as HTMLInputElement;
            setSortBy(value as ReportsOrderBy);
          }}
        />
      )}
      {reports.length ? (
        <M.TileOptionList
          className='cards-list'
          id={REPORTS_PAGE.reportCardList}
          data-testid={REPORTS_PAGE.reportCardList}
        >
          {reports.sort(sortByOptions[sortBy].compare).map((report, index) => (
            <ReportCard
              key={report.id}
              report={report}
              position={index + 1}
              id={`report-card-${report.reportOrder}`}
            />
          ))}
        </M.TileOptionList>
      ) : (
        <EmptyState applicantId={applicantId} />
      )}
    </M.Container.Col>
  );
};
